import type { Grade } from "./types"

/** Point cost of one warning. */
const WARNING_WEIGHT = 5
/** Info issues are advisory noise; their total cost is capped. */
const INFO_PENALTY_CAP = 15

/**
 * An issue as the scorer sees it: a level, and — when the caller can supply it
 * — the D-472 channel of the STAGE that produced it.
 *
 * `channel` is OPTIONAL and its absence means `"safety"`. That default is
 * deliberate and fail-closed: a caller that cannot say which channel an issue
 * came from gets the old, stricter behaviour rather than a silent amnesty.
 */
export interface ScorableIssue {
  level: string
  channel?: "safety" | "advisory"
}

/**
 * R3 (operator decision, 2026-08-09) — an ADVISORY error must not produce the
 * reject cliff.
 *
 * Before: any `error` returned 0, so a single advisory-stage error graded the
 * whole artifact `reject`. That is not a scoring nicety — it is half of the
 * mechanism that forged a regression: twelve `ux-interactive-muted-color`
 * firings (a Tailwind text-colour preference, advisory `ux` stage) zeroed a
 * batch's scores, the zero was read as a product regression, and a working
 * prompt change was withdrawn on the strength of it. Task 2 corrected that one
 * rule's severity and split `summary.errors` into `safetyErrors` /
 * `advisoryErrors`; this is the other half — the consumer.
 *
 * After: a safety-channel error still returns 0 (the safety contract is a
 * hard block and nothing here softens it), while an advisory-channel error is
 * priced at the SAME weight as a warning. Not a new constant, and not free:
 * an advisory error is a real finding on the quality channel, it simply cannot
 * be the thing that fails a release.
 *
 * NOT changed: no stage's `passed` computation. `passed` remains
 * `!issues.some(i => i.level === "error")` per stage, so `runSafetyContract`
 * hard-blocks exactly what it hard-blocked before.
 */
export function computeScore(issues: Array<ScorableIssue>): number {
  let score = 100
  let infoPenalty = 0
  for (const issue of issues) {
    if (issue.level === "error") {
      if (issue.channel === "advisory") {
        score -= WARNING_WEIGHT
        continue
      }
      return 0
    }
    if (issue.level === "warning") score -= WARNING_WEIGHT
    if (issue.level === "info") infoPenalty += 1
  }
  // Cap info penalty at 15 points — info issues are advisory, not defects
  score -= Math.min(infoPenalty, INFO_PENALTY_CAP)
  return Math.max(0, score)
}

export function computeGrade(score: number): Grade {
  if (score >= 80) return "pass"
  if (score >= 60) return "warn"
  return "reject"
}
